import {
  getPDPUrl,
  mergeLinkWithIngredients,
  extractIngredientsFromList,
} from '../url';
import { getTitle, getContainerType, getCommonProps } from '../commonHelpers';
import { getCarouselTitleWithFallback } from '../carouselTitleHelpers';
import cardMethods from '../../cardMethods';
import {
  CARD_TYPES,
  DYNAMIC_CONTENT_SUPPORTED_CARD_TYPES,
} from '../../../../constants';

/**
 *    @description merges carousel data with translated labels and sets section headline title
 *    @param {Object} cardData prepared component data (required)
 *    @param {Object} translations  object with filled in labels for specific component (required)
 *    @returns {Object} mergedData returns data for product carousel with translated labels
 *    getCarouselWithTitle: ({ cardData, translations }) => mergedData,
 * */
export const getCarouselWithTitle = ({ cardData = {}, translations = {} }) => ({
  ...cardData,
  ...translations,
  sectionHeadline: {
    ...cardData.sectionHeadline,
    title: getCarouselTitleWithFallback({ cardData }),
  },
});

/**
 *    @description merges together URLs, translations and card data into final props for the component (for section headline and all carousel titles)
 *    @param {Object} cardData prepared component data (required)
 *    @param {Object} translations object with filled in labels for specific component
 *    @param {[Object, string][]} urls array with URL ingredients and corresponding URL
 *    @returns {Object} mergedData data merged with translated labels and URLs instead of ingredients, finally ready props for component
 *    mergeSectionHeadlineTitle: ({ cardData, translations, urls }) => mergedData,
 * */
export const mergeSectionHeadlineTitle = ({
  cardData,
  /**
   * Refactoring note: It is simultaneously used as a helper in various cards and as a standalone `mergeProperties` method for section headline.
   * As a result in one place cardData argument is a result of `prepareCardData` and in others it is not a cardData but card's `cardData.sectionHeadline`
   * No plan to fix now. More details: CICLP-3100
   */
  urls,
}) => ({
  ...cardData,
  actions: cardData?.actions?.map(action =>
    mergeLinkWithIngredients({ action, property: 'destinationId', urls }),
  ),
});

/**
 *    @description merges together URLs, translations and card data into final props for the product container (external collection)
 *    @param {Object} cardData prepared component data (required)
 *    @param {Object} translations object with filled in labels for specific component
 *    @param {[Object, string][]} urls array with URL ingredients and corresponding URL
 *    @param {string} countryCode code for PDP URLs manual generation
 *    @param {string} languageRegion for PDP URLs manual generation
 *    @param {string} languageCountryPath for PDP URLs manual generation
 *    @param {Object} languageMappings
 *    @returns {Object} mergedProductContainerData data merged with translated labels and URLs instead of ingredients, finally ready props for component
 *    mergeCarouselProperties: ({ cardData, translations, urls, countryCode, languageRegion, languageCountryPath }) => mergedProductContainerData,
 * */
export const mergeCarouselProperties = ({
  cardData,
  translations,
  urls,
  countryCode,
  languageRegion,
  languageCountryPath,
  languageMappings,
}) => {
  const { slides = [], sectionHeadline, ...restCardData } = cardData;
  return {
    ...restCardData,
    ...translations,
    localeForCurrency: languageMappings?.hreflang,
    sectionHeadline: mergeSectionHeadlineTitle({
      cardData: sectionHeadline,
      translations,
      urls,
    }),
    slides: slides.map(slide => ({
      ...slide,
      country: countryCode,
      // ProductCard link prop URL from urlIngredients
      link: {
        href: getPDPUrl({
          countryCode,
          languageCountryPath,
          languageRegion,
          pdpType: slide?.pdpType,
          urlIngredients: slide?.urlIngredients,
          urls,
        }),
        title: slide.title,
      },
      url: getPDPUrl({
        countryCode,
        languageCountryPath,
        languageRegion,
        pdpType: slide?.pdpType,
        urlIngredients: slide?.urlIngredients,
        urls,
      }),
    })),
  };
};

/**
 *    @description populates card with url ingredients
 *    @param {Object} card prepared component data (required)
 *    @returns {Object} url ingredients data
 * */

export const getGalleryUrlIngredients = (card = {}) => [
  ...extractIngredientsFromList([
    ...(card?.sectionHeadline?.actions ?? []),
    ...(card?.featuredCard
      ? [card?.featuredCard, ...(card?.featuredCard?.actionButtons ?? [])]
      : []),
  ]),
  ...(card?.slides ?? []).flatMap(slide => [
    ...extractIngredientsFromList([slide, ...(slide?.actionButtons ?? [])]),
    ...extractIngredientsFromList(slide?.urls),
  ]),
];

const prepareGalleryCardSubActions = (card, bannerCardId) => {
  if (card) {
    card.isInCarousel = 'true';
  }
  card?.actionButtons?.forEach(button => {
    if (button?.analytics) {
      button.analytics.inCarousel = true;
    }

    if (bannerCardId) {
      button.bannerCardId = bannerCardId;
    }
  });
  return card;
};

/**
 *    @description prepare slides data for a card, populate card with headline
 *    @param {Object} cardData prepared component data (required)
 *    @param {Object} data initial card data (required)
 *    @param {String} containerType one of CARD_TYPES
 *    @returns {Object} preparedFilmstripCardData, finally ready props for component
 * */

export const prepareGalleryCardData = (
  cardData,
  data,
  containerType,
  bannerCardId,
) => {
  const sectionHeadline = getTitle(cardData);

  const nodes = cardData?.nodes ?? [];
  const filtered =
    containerType === CARD_TYPES.SLIDESHOW
      ? nodes.filter(
          slideId => getContainerType(data.nodes[slideId]) === CARD_TYPES.IMAGE,
        )
      : nodes;
  const slides = filtered.map(slideId =>
    prepareGalleryCardSubActions(
      cardMethods[getContainerType(data.nodes[slideId])]?.prepareCardData(
        data.nodes[slideId],
        data,
        { withDefaultImageHeight: containerType !== CARD_TYPES.FILMSTRIP },
      ),
      bannerCardId,
    ),
  );

  return {
    ...getCommonProps(cardData),
    fallbacks: cardData?.properties?.fallbacks,
    loop: cardData?.properties?.loop,
    sectionHeadline,
    slides,
    speed: cardData?.properties?.speed,
  };
};

/**
 *    @description merges together URLs, translations and card data into final props for the filmstrip (filmstrip, related and slideshow)
 *    @param {Object} cardData prepared component data (required)
 *    @param {[Object, string][]} urls array with URL ingredients and corresponding URL
 *    @param {string} pageType type of the page (article or thread)
 *    @param {Object} translations list of translations
 *    @param {Object} restOptions rest rendering options
 *    @returns {Object} mergedFilmstripProperties data merged with translated labels and URLs instead of ingredients, finally ready props for component
 *    mergeGalleryProperties: ({ cardData, urls }) => mergedFilmstripProperties,
 * */

export const mergeGalleryProperties = ({
  cardData = {},
  urls = [],
  pageType,
  translations,
  ...restOptions
}) => {
  const {
    slides = [],
    sectionHeadline,
    featuredCard,
    ...restCardData
  } = cardData;
  return {
    translations,
    ...restCardData,
    pageType,
    sectionHeadline: mergeSectionHeadlineTitle({
      cardData: sectionHeadline,
      translations,
      urls,
    }),
    ...(featuredCard && {
      featuredCard: cardMethods[featuredCard?.containerType]?.mergeProperties({
        cardData: featuredCard,
        pageType,
        translations,
        urls,
        ...restOptions,
      }),
    }),
    slides: slides.map(slide =>
      slide?.containerType
        ? {
            ...translations,
            ...cardMethods[slide?.containerType]?.mergeProperties({
              cardData: slide,
              pageType,
              translations,
              urls,
              ...restOptions,
            }),
          }
        : slide,
    ),
  };
};

const prepareEditorialSlide = (
  { coverCard, tagging, publish },
  preferredOrientation = {
    large: 'landscape',
    medium: 'landscape',
    small: 'landscape',
  },
) => ({
  ...cardMethods[getContainerType(coverCard)].prepareCardData({
    ...coverCard,
    properties: {
      ...coverCard?.properties,
      preferredOrientation,
    },
  }),
  cardLinkId: `${coverCard?.id}_card-link`,
  destinationType: 'ARTICLE',
  taxonomyAttributes: tagging?.taxonomyAttributes,
  urlIngredients: {
    pageId: publish?.collections?.[0],
    type: 'ARTICLE',
  },
});

export const prepareDynamicContentCarousel = (threads, preferredOrientation) =>
  threads
    .filter(thread =>
      DYNAMIC_CONTENT_SUPPORTED_CARD_TYPES.includes(
        getContainerType(thread?.publishedContent?.properties?.coverCard),
      ),
    )
    .map(thread =>
      prepareEditorialSlide(
        thread?.publishedContent?.properties,
        preferredOrientation,
      ),
    );
